/**
 * @file useCarouselKeylines.ts
 * Custom hook calculating M3 Carousel keyline matrices, item widths,
 * snap positions, dynamic clip-path mask percentages, and parallax offsets.
 *
 * Specification: Material Design 3 Carousel Specs
 * @see docs/m3/carousel/Carousel_Specs_Material_Design_3.md
 */

import { useMemo } from "react";
import { CarouselTokens } from "../carousel.tokens";
import type { CarouselKeylineResult, CarouselLayout } from "../carousel.types";

export interface UseCarouselKeylinesOptions {
	containerWidth: number;
	preferredItemWidth?: number;
	layout?: CarouselLayout;
	itemSpacing?: number;
	contentPadding?: number;
	totalItems: number;
	scrollX?: number;
}

export function useCarouselKeylines({
	containerWidth,
	preferredItemWidth = 300,
	layout = "multi-browse",
	itemSpacing = CarouselTokens.itemSpacing,
	contentPadding = CarouselTokens.contentPadding,
	totalItems,
	scrollX: _scrollX = 0,
}: UseCarouselKeylinesOptions): CarouselKeylineResult & {
	getItemMaskClip: (index: number) => number;
	getItemParallaxOffset: (index: number) => number;
	getItemWidth: (index: number) => number;
} {
	return useMemo(() => {
		const safeContainerWidth = Math.max(containerWidth, 100);

		// Clamp small item size between 40px and 56px per M3 spec
		const smallWidth = Math.min(
			Math.max(
				CarouselTokens.smallItemMin,
				Math.round(safeContainerWidth * 0.1),
			),
			CarouselTokens.smallItemMax,
		);

		let largeWidth = preferredItemWidth;
		let mediumWidth = Math.round(preferredItemWidth * 0.65);

		// Adjust sizes according to layout variant
		switch (layout) {
			case "hero": {
				const availableWidth =
					safeContainerWidth - contentPadding * 2 - smallWidth - itemSpacing;
				largeWidth = Math.max(160, availableWidth);
				mediumWidth = largeWidth;
				break;
			}
			case "center-aligned-hero": {
				const availableWidth =
					safeContainerWidth -
					contentPadding * 2 -
					smallWidth * 2 -
					itemSpacing * 2;
				largeWidth = Math.max(160, availableWidth);
				mediumWidth = largeWidth;
				break;
			}
			case "uncontained":
			case "uncontained-multi-aspect": {
				largeWidth = Math.min(
					preferredItemWidth,
					safeContainerWidth - contentPadding * 2,
				);
				mediumWidth = largeWidth;
				break;
			}
			case "full-screen": {
				largeWidth = safeContainerWidth;
				mediumWidth = safeContainerWidth;
				break;
			}
			default: {
				// multi-browse layout sizing
				const maxLargeWidth =
					safeContainerWidth - contentPadding * 2 - smallWidth - itemSpacing;
				largeWidth = Math.min(preferredItemWidth, Math.max(160, maxLargeWidth));
				mediumWidth = Math.max(smallWidth + 16, Math.round(largeWidth * 0.65));
				break;
			}
		}

		// Calculate initial keylines array based on layout
		const keylines: CarouselKeylineResult["keylines"] = [];
		if (layout === "hero") {
			const smallMask = Math.max(
				0,
				Math.min(
					100,
					Math.round(((largeWidth - smallWidth) / largeWidth) * 100),
				),
			);
			keylines.push(
				{
					offset: contentPadding,
					width: largeWidth,
					type: "large",
					maskClip: 0,
				},
				{
					offset: contentPadding + largeWidth + itemSpacing,
					width: smallWidth,
					type: "small",
					maskClip: smallMask,
				},
			);
		} else if (layout === "center-aligned-hero") {
			const smallMask = Math.max(
				0,
				Math.min(
					100,
					Math.round(((largeWidth - smallWidth) / largeWidth) * 100),
				),
			);
			keylines.push(
				{
					offset: contentPadding,
					width: smallWidth,
					type: "small",
					maskClip: smallMask,
				},
				{
					offset: contentPadding + smallWidth + itemSpacing,
					width: largeWidth,
					type: "large",
					maskClip: 0,
				},
				{
					offset:
						contentPadding +
						smallWidth +
						itemSpacing +
						largeWidth +
						itemSpacing,
					width: smallWidth,
					type: "small",
					maskClip: smallMask,
				},
			);
		} else if (layout === "multi-browse") {
			const mediumMask = Math.max(
				0,
				Math.min(
					100,
					Math.round(((largeWidth - mediumWidth) / largeWidth) * 100),
				),
			);
			const smallMask = Math.max(
				0,
				Math.min(
					100,
					Math.round(((largeWidth - smallWidth) / largeWidth) * 100),
				),
			);
			const availableWidth = safeContainerWidth - contentPadding * 2;
			const numLarge = Math.max(
				1,
				Math.floor((availableWidth + itemSpacing) / (largeWidth + itemSpacing)),
			);

			let offset = contentPadding;
			for (let k = 0; k < numLarge; k++) {
				keylines.push({
					offset,
					width: largeWidth,
					type: "large",
					maskClip: 0,
				});
				offset += largeWidth + itemSpacing;
			}
			keylines.push({
				offset,
				width: mediumWidth,
				type: "medium",
				maskClip: mediumMask,
			});
			offset += mediumWidth + itemSpacing;
			keylines.push({
				offset,
				width: smallWidth,
				type: "small",
				maskClip: smallMask,
			});
		} else {
			keylines.push({
				offset: contentPadding,
				width: largeWidth,
				type: "large",
				maskClip: 0,
			});
		}

		// Helper to calculate initial rest width for keyline alignment
		const getItemRestWidth = (i: number): number => {
			if (layout === "full-screen") return safeContainerWidth;
			if (layout === "uncontained") return largeWidth;
			if (layout === "uncontained-multi-aspect") {
				const ratios = [1.77, 1.33, 1.0, 0.56];
				const ratio = ratios[i % ratios.length];
				return Math.round(Math.min(largeWidth, 220 * ratio));
			}
			if (layout === "hero") return i === 0 ? largeWidth : smallWidth;
			if (layout === "center-aligned-hero")
				return i === 0 || i === totalItems - 1 ? smallWidth : largeWidth;

			// multi-browse cycle pattern: Large, Large, Medium, Small
			const pattern = [largeWidth, largeWidth, mediumWidth, smallWidth];
			return pattern[i % pattern.length];
		};

		// Calculate track start positions for each item
		const snapPositions: number[] = [];
		let currentOffset = 0;
		const itemLayoutWidth =
			layout === "full-screen" ? safeContainerWidth : largeWidth;
		for (let i = 0; i < totalItems; i++) {
			snapPositions.push(currentOffset);
			currentOffset += itemLayoutWidth + itemSpacing;
		}

		// Stable item width calculation for layout composition — all items render at largeWidth capacity
		const getItemWidth = (_index: number): number => {
			if (layout === "full-screen") return safeContainerWidth;
			return largeWidth;
		};

		const getItemMaskClip = (index: number): number => {
			if (
				layout === "full-screen" ||
				layout === "uncontained" ||
				layout === "uncontained-multi-aspect"
			) {
				return 0;
			}
			const itemW = getItemRestWidth(index);
			if (itemW >= largeWidth || largeWidth <= 0) return 0;
			return Math.max(
				0,
				Math.min(100, Math.round(((largeWidth - itemW) / largeWidth) * 100)),
			);
		};

		const getItemParallaxOffset = (index: number): number => {
			const maskClip = getItemMaskClip(index);
			if (maskClip <= 0) return 0;
			return calculateParallaxOffset(0, maskClip, largeWidth);
		};

		let maxAccumulatedShift = 0;
		if (layout === "hero") {
			maxAccumulatedShift = Math.max(
				0,
				(totalItems - 1) * (largeWidth - smallWidth),
			);
		} else if (layout === "center-aligned-hero") {
			maxAccumulatedShift = Math.max(
				0,
				Math.min(totalItems, 2) * (largeWidth - smallWidth),
			);
		} else if (layout === "multi-browse") {
			const availableWidth = safeContainerWidth - contentPadding * 2;
			const numLarge = Math.max(
				1,
				Math.floor((availableWidth + itemSpacing) / (largeWidth + itemSpacing)),
			);
			const nonLargeItems = Math.max(0, totalItems - numLarge);
			if (nonLargeItems > 0) {
				const mediumClipped = largeWidth - mediumWidth;
				const smallClipped = largeWidth - smallWidth;
				const numSmall = Math.max(0, nonLargeItems - 1);
				maxAccumulatedShift = mediumClipped + numSmall * smallClipped;
			}
		}

		return {
			largeWidth,
			mediumWidth,
			smallWidth,
			keylines,
			snapPositions,
			maxAccumulatedShift,
			getItemWidth,
			getItemMaskClip,
			getItemParallaxOffset,
		};
	}, [
		containerWidth,
		preferredItemWidth,
		layout,
		itemSpacing,
		contentPadding,
		totalItems,
	]);
}

/**
 * Pure calculation helper: Dynamic mask clip percentage for an item based on its viewport X offset.
 */
export function calculateMaskClipForOffset(
	itemLeftInViewport: number,
	containerWidth: number,
	largeWidth: number,
	mediumWidth: number,
	smallWidth: number,
	contentPadding: number = 16,
	layout: CarouselLayout = "multi-browse",
): number {
	if (
		layout === "full-screen" ||
		layout === "uncontained" ||
		layout === "uncontained-multi-aspect" ||
		largeWidth <= 0
	) {
		return 0;
	}

	const mediumMask = Math.max(
		0,
		Math.min(100, ((largeWidth - mediumWidth) / largeWidth) * 100),
	);
	const smallMask = Math.max(
		0,
		Math.min(100, ((largeWidth - smallWidth) / largeWidth) * 100),
	);

	// Trailing threshold: item approaching or passing right edge
	const trailingFocalThreshold =
		containerWidth - contentPadding - mediumWidth - (largeWidth - mediumWidth);
	const trailingMediumThreshold = containerWidth - contentPadding - smallWidth;
	const trailingEndThreshold = containerWidth - contentPadding;

	// Leading threshold: item scrolling off left edge
	const leadingThreshold = contentPadding;

	// Case 1: Item scrolling off the left edge
	if (itemLeftInViewport < leadingThreshold) {
		const dist = leadingThreshold - itemLeftInViewport;
		const progress = Math.min(1, Math.max(0, dist / largeWidth));
		return Math.min(100, Math.max(0, progress * smallMask));
	}

	// Case 2: Item approaching the right edge
	if (itemLeftInViewport > trailingFocalThreshold) {
		if (itemLeftInViewport < trailingMediumThreshold) {
			const range = Math.max(
				1,
				trailingMediumThreshold - trailingFocalThreshold,
			);
			const progress = Math.min(
				1,
				Math.max(0, (itemLeftInViewport - trailingFocalThreshold) / range),
			);
			return progress * mediumMask;
		}
		const range = Math.max(1, trailingEndThreshold - trailingMediumThreshold);
		const progress = Math.min(
			1,
			Math.max(0, (itemLeftInViewport - trailingMediumThreshold) / range),
		);
		return mediumMask + progress * (smallMask - mediumMask);
	}

	// Case 3: In focal zone
	return 0;
}

/**
 * Pure calculation helper: Inner content parallax translation in px based on mask clip percentage.
 */
export function calculateParallaxOffset(
	_itemLeftInViewport: number,
	maskClipPercent: number,
	largeWidth: number,
): number {
	if (maskClipPercent <= 0 || largeWidth <= 0) return 0;
	return -Math.round((maskClipPercent / 100) * (largeWidth * 0.2));
}
